import { useMemo, useCallback } from 'react';
import { Flex, Box, Paragraph } from 'theme-ui';
import { useInView } from 'react-intersection-observer';

import { Picture, Link, Locale } from '@snippets';
import {
  useProductItemImages,
  useDataLayerActions,
  useGatedProduct,
} from '@hooks';
import store, { useRecoilValue } from '@store';
import {
  useProductByHandle,
  useSettings,
  useLocalizedVariant,
} from '@backpackjs/storefront';
import { useActions, useVariables } from '@tapcart/webbridge-react';
import { AddToCart } from './AddToCart';
import { themed } from './ProductTile.theme';

export const ProductTile = themed(({ theme, item, index }) => {
  // Extract product and admin-entered fields
  const { product, color, size } = item || {};
  const { sendClickProductItemEvent } = useDataLayerActions();
  const gatedPage = useRecoilValue(store.gatedPage);
  const settings = useSettings();
  const { gatedPages } = { ...settings?.gatedPages } || {};

  const { enableFullProductAccess } = useGatedProduct({ product, gatedPage });
  const { isInitialized } = useVariables();
  const { openProduct, addToCart: addToTapCart } = useActions();

  const { ref, inView } = useInView({
    rootMargin: '400px',
    triggerOnce: true,
  });
  const { product: fullProduct } = useProductByHandle({
    handle: product?.handle || '',
    fetchOnMount: inView,
  });

  const selectedVariant = useMemo(() => {
    return fullProduct?.variants?.[0];
  }, [fullProduct?.id]);

  const { localized } = useLocalizedVariant({ variant: selectedVariant });

  const hasMultipleVariants = useMemo(() => {
    if (!fullProduct) return false;
    let count = 0;
    let hasMultiple = false;
    fullProduct.variants?.find((variant) => {
      if (variant.product.handle === fullProduct.handle) {
        count += 1;
        if (count > 1) {
          hasMultiple = true;
          return true;
        }
      }
      return false;
    });
    return hasMultiple;
  }, [fullProduct?.id]);

  const hiddenProductHandles = useMemo(() => {
    return (
      !!gatedPages?.length &&
      gatedPages.reduce((carry, { enable, page }) => {
        if (enable) {
          const { pages } = { ...page };
          const _products = pages?.reduce((_carry, _page) => {
            if (_page?.enableFullProductAccess) {
              return _carry;
            }
            return [
              ..._carry,
              ...(_page?.products?.map(({ p }) => p?.handle)?.filter(Boolean) ||
                []),
            ];
          }, []);
          return [...carry, ...(_products || [])];
        }
        return carry;
      }, [])
    );
  }, [gatedPages]);

  const intellisuggestTrackClick = useCallback(() => {
    if (document.images) {
      const apiUrl = `${window.location.protocol || 'http:'}//${
        process.env.NEXT_PUBLIC_SEARCHSPRING_SITE_ID
      }.a.searchspring.io/api/`;

      const imgTag = new Image();
      imgTag.src = `${apiUrl}track/track.json?d=${
        fullProduct.intellisuggestData
      }&s=${fullProduct.intellisuggestSignature}&u=${encodeURIComponent(
        `${process.env.SITE_URL}/products/${fullProduct.handle}`
      )}&r=${encodeURIComponent(document.referrer)}&bgfilter.ss_mobile_only=0`;
    }

    return true;
  }, [fullProduct?.id]);

  const handleClick = useCallback(
    async (event) => {
      if (isInitialized && fullProduct?.legacyResourceId) {
        event.preventDefault();
        openProduct({
          productId: `${fullProduct.legacyResourceId}`,
        });
        return;
      }
      sendClickProductItemEvent({
        isSearchResult: false,
        listIndex: index,
        localized,
        product: fullProduct,
        searchTerm: null,
        selectedVariant,
      });
      intellisuggestTrackClick();
    },
    [index, localized, fullProduct?.id, selectedVariant?.id, isInitialized]
  );

  // Get product images using the existing hook
  const { primary } = useProductItemImages({ product: fullProduct });

  // Basic product info with fallbacks
  const productTitle = fullProduct?.title || fullProduct?.handle || 'Product';
  const displayColor = color || '';
  const displaySize = size || '';
  const productImage = useMemo(() => {
    if (primary?.originalSrc) {
      return primary;
    }
    // Fallback to first image in product data if primary is not available
    return product?.data?.images?.[0] || null;
  }, [primary?.originalSrc, product?.data?.images]);

  return (
    <Flex data-comp={ProductTile.displayName} sx={theme.wrapper} ref={ref}>
      <Box
        sx={{
          ...theme.content,
          height: ['420px', '450px'], // Fixed height for all tiles
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Product image using Shopify data */}
        <Box
          sx={{
            width: '100%',
            height: ['200px', '250px'],
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0, // Prevent image from shrinking
          }}
        >
          {productImage?.originalSrc ? (
            <Picture
              alt={productTitle}
              images={[
                {
                  src: productImage.originalSrc,
                  width: 300,
                  ratio: 1,
                },
              ]}
              imageBgColor="lightGray"
              width={productImage.width}
              height={productImage.height}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              imageSx={{ objectFit: 'contain' }}
            />
          ) : (
            <Box
              sx={{
                color: '#666',
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              Product Image
            </Box>
          )}
        </Box>

        {/* Product details */}
        <Box
          sx={{
            flex: 1,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 2,
          }}
        >
          <Box
            sx={{
              fontFamily: 'body',
              fontWeight: 600,
              fontSize: ['16px', '18px'],
              color: '#000000',
              mb: 2,
            }}
          >
            {displayColor || productTitle}
          </Box>

          {displaySize && (
            <Box
              sx={{
                fontFamily: 'body',
                fontSize: ['14px', '15px'],
                color: '#666666',
              }}
            >
              {displaySize}
            </Box>
          )}
        </Box>

        {/* Variant price */}
        {(selectedVariant || fullProduct?.variants?.[0]) && (
          <Locale.Variant
            variant={selectedVariant || fullProduct?.variants?.[0]}
            sx={{
              display: 'none',
              p: {
                fontSize: '15px',
              },
            }}
            className="flex gap-1 pl-1"
          >
            <Locale.Price>
              {({ priceV2, compareAtPriceV2, isOnSale }) => {
                return isOnSale ? (
                  <Flex sx={{ variant: 'flex.row.end.start', gap: '4px' }}>
                    <Paragraph>• {priceV2.locale}</Paragraph>
                    <Paragraph
                      sx={{
                        textDecoration: 'line-through',
                        color: 'mediumDarkGray',
                      }}
                    >
                      {compareAtPriceV2.locale}
                    </Paragraph>
                  </Flex>
                ) : (
                  <Paragraph>• {priceV2.locale}</Paragraph>
                );
              }}
            </Locale.Price>
          </Locale.Variant>
        )}

        {/* Add to cart button */}
        {!hasMultipleVariants &&
          enableFullProductAccess &&
          hiddenProductHandles?.indexOf(fullProduct?.handle) === -1 && (
            <AddToCart
              product={fullProduct}
              selectedVariant={selectedVariant}
              isProductItem
              style={theme.atc}
              addToTapCart={addToTapCart}
              isInitialized={isInitialized}
            />
          )}

        {hasMultipleVariants && enableFullProductAccess && (
          <Link
            data-comp="SeeDetails"
            href={`/products/${fullProduct?.handle}`}
            aria-label={`View details for ${fullProduct?.title}`}
            variant="buttons.primary"
            sx={theme.atc}
            onClick={handleClick}
          >
            See Details
          </Link>
        )}
      </Box>
    </Flex>
  );
});

ProductTile.displayName = 'ProductCarouselToggle-ProductTile';
